import { Box, Typography } from '@mui/material';
import { format } from 'date-fns-tz';
import { getDateFormatter, LongDateTimeFormat, ShortDateFormat } from '@utils/date';
import {
  QuoteActivityItemType,
  activityItemTypeText,
  NewQuoteRatingItem,
  NewNoteItem,
  NewQuoteStatusChangeItem,
  NewDocumentItem,
  ratingText,
  NewUnderwritingActivityItem,
} from '@pages/QuoteDetails/sidebarItems/summary/quoteActivity/QuoteActivityItem.constants';
import { UserAccount } from '@components/container/UserAccount';
import { HtmlContent } from '@components/noteMessage/CardNote';
import { quoteRatingStatus } from '@models/quoteRating';
import { getNameInitials } from '@utils/regex';
import { ViewQuoteRatingSummaryButton } from '@pages/QuoteDetails/sidebarItems/summary/quoteActivity/ViewQuoteRatingSummaryButton';
import { getRatingErrors } from '@pages/QuoteDetails/helpers/getRatingErrors';
import { OverflowTooltip } from '@components/tooltip';
import { Theme } from '@mui/material/styles';
import { TableGridBaseColDef } from '@components/table/types/TableColumnDef.type';
import { ClockFormat } from '@lio/rule-engine';

type ActivityItem =
  | NewQuoteRatingItem
  | NewNoteItem
  | NewQuoteStatusChangeItem
  | NewDocumentItem
  | NewUnderwritingActivityItem;

type TActivityColumns = {
  clockFormat: ClockFormat;
  isInternalUser: boolean;
  theme: Theme;
};

// FUTURE FULLSCREEN TODO: this needs to be redone so it is more dynamic/shared with the sidebar. Having to add new columns may persist but
// things like 'content and 'user' could prove to be more shared
export const getQuoteActivityColumns = ({ clockFormat, isInternalUser, theme }: TActivityColumns) => {
  const columns: TableGridBaseColDef<ActivityItem>[] = [
    {
      field: 'createdAt',
      headerName: 'Date',
      width: 160,
      sortable: false,
      renderCell: ({ row }) => {
        const date = format(new Date(row.createdAt), getDateFormatter(clockFormat, LongDateTimeFormat));
        return (
          <OverflowTooltip tooltipText={date}>
            <Typography variant='bodySmall'>{date}</Typography>
          </OverflowTooltip>
        );
      },
    },
    {
      field: 'activityItemType',
      headerName: 'Type',
      width: 100,
      sortable: false,
      renderCell: ({ row }) => {
        if (row.activityItemType === QuoteActivityItemType.UnderwritingActivity) {
          return (
            <Typography variant='bodySmall' sx={{ fontWeight: 'medium' }}>
              {(row as NewUnderwritingActivityItem).resolvedAt ? 'Activity Resolved' : 'Activity Created'}
            </Typography>
          );
        }

        return (
          <Typography variant='bodySmall' sx={{ fontWeight: 'medium' }}>
            {activityItemTypeText[row.activityItemType]}
          </Typography>
        );
      },
    },
    {
      field: 'user',
      headerName: 'User',
      width: 180,
      sortable: false,
      renderCell: ({ row }) => {
        let firstName = '',
          lastName = '';
        switch (row.activityItemType) {
          case QuoteActivityItemType.QuoteStatusChange:
            if ('changedByUser' in row) {
              firstName = row.changedByUser?.firstName ?? '';
              lastName = row.changedByUser?.lastName ?? '';
            }
            break;
          case QuoteActivityItemType.QuoteRating:
            if ('ratedByUser' in row) {
              firstName = row.ratedByUser?.firstName ?? '';
              lastName = row.ratedByUser?.lastName ?? '';
            }
            break;
          case QuoteActivityItemType.Document:
            if ('documentGeneration' in row) {
              firstName = row.documentGeneration?.triggeredByUser?.firstName ?? '';
              lastName = row.documentGeneration?.triggeredByUser?.lastName ?? '';
            }
            break;
          case QuoteActivityItemType.Note:
          case QuoteActivityItemType.Message:
            if ('createdByUser' in row) {
              firstName = row.createdByUser?.firstName ?? '';
              lastName = row.createdByUser?.lastName ?? '';
            }
            break;
          case QuoteActivityItemType.UnderwritingActivity:
            if ('createdByUser' in row) {
              firstName = row.createdByUser?.firstName ?? '';
              lastName = row.createdByUser?.lastName ?? '';
            }
            break;
        }
        const getAvatarColor = () => {
          switch (row.activityItemType) {
            case QuoteActivityItemType.Note:
            case QuoteActivityItemType.Message:
              return theme.palette.primary.main;
            case QuoteActivityItemType.QuoteStatusChange:
            case QuoteActivityItemType.QuoteRating:
              return theme.palette.secondary.main;
            case QuoteActivityItemType.Document:
              return theme.palette.info.main;
            default:
              return theme.palette.common.gray;
          }
        };
        return (
          <Box sx={{ display: 'flex', alignContent: 'center', height: '100%' }}>
            <UserAccount
              boxProps={{
                sx: {
                  display: 'flex',
                  alignItems: 'center',
                },
              }}
              avatarProps={{
                sx: (theme) => ({
                  ...theme.icons.sm,
                  fontSize: '12px',
                  fontWeight: 400,
                  backgroundColor: getAvatarColor(),
                  marginRight: '5px',
                }),
                children: getNameInitials(`${firstName} ${lastName}`),
              }}
            >
              <Typography variant='caption'>
                {firstName} {lastName}
              </Typography>
            </UserAccount>
          </Box>
        );
      },
    },
    {
      field: 'content',
      headerName: 'Content',
      minWidth: 350,
      flex: 1,
      renderCell: ({ row }) => {
        switch (row.activityItemType) {
          case QuoteActivityItemType.QuoteStatusChange:
            if ('newQuoteStatus' in row) {
              return (
                <Typography
                  variant='bodySmall'
                  sx={{
                    color: 'text.main',
                    whiteSpace: 'normal',
                    wordBreak: 'break-word',
                    lineHeight: 1.4,
                    overflow: 'visible',
                    textOverflow: 'unset',
                  }}
                >
                  Status changed to {row.newQuoteStatus?.label || 'Unknown'}
                </Typography>
              );
            }
            return 'Status changed';
          case QuoteActivityItemType.QuoteRating:
            if ('status' in row && 'termPremium' in row) {
              const currentItem = row as NewQuoteRatingItem;
              const { status } = currentItem;
              const ratingErrors = getRatingErrors(currentItem);

              if (status === quoteRatingStatus.Succeeded) {
                return (
                  <OverflowTooltip
                    tooltipText={ratingText[quoteRatingStatus.Succeeded](
                      currentItem.termPremium,
                      currentItem.termTaxFeeSurcharge,
                    )}
                  >
                    <Typography
                      variant='bodySmall'
                      sx={{
                        color: 'text.main',
                        whiteSpace: 'normal',
                        wordBreak: 'break-word',
                        lineHeight: 1.4,
                        overflow: 'visible',
                        textOverflow: 'unset',
                      }}
                    >
                      {ratingText[quoteRatingStatus.Succeeded](
                        currentItem.termPremium,
                        currentItem.termTaxFeeSurcharge,
                      )}
                    </Typography>
                  </OverflowTooltip>
                );
              } else if (status === quoteRatingStatus.Failed) {
                return (
                  <OverflowTooltip tooltipText={ratingText[quoteRatingStatus.Failed](ratingErrors[0])}>
                    <Typography
                      variant='bodySmall'
                      sx={{
                        color: 'text.main',
                        whiteSpace: 'normal',
                        wordBreak: 'break-word',
                        lineHeight: 1.4,
                        overflow: 'visible',
                        textOverflow: 'unset',
                      }}
                    >
                      {ratingText[quoteRatingStatus.Failed](ratingErrors[0])}
                    </Typography>
                  </OverflowTooltip>
                );
              }
              return (
                <Typography
                  variant='bodySmall'
                  sx={{
                    color: 'text.main',
                    whiteSpace: 'normal',
                    wordBreak: 'break-word',
                    lineHeight: 1.4,
                    overflow: 'visible',
                    textOverflow: 'unset',
                  }}
                >
                  {status}
                </Typography>
              );
            }
            return 'Rating';
          case QuoteActivityItemType.Note:
          case QuoteActivityItemType.Message:
            if ('body' in row) {
              try {
                return (
                  <OverflowTooltip tooltipText={row.body}>
                    <Box
                      sx={{
                        whiteSpace: 'normal',
                        wordBreak: 'break-word',
                        lineHeight: 1.4,
                        overflow: 'visible',
                        textOverflow: 'unset',
                      }}
                    >
                      <HtmlContent body={row.body} />
                    </Box>
                  </OverflowTooltip>
                );
              } catch {
                return (
                  <OverflowTooltip tooltipText={row.body}>
                    <Typography
                      variant='bodySmall'
                      sx={{
                        color: 'text.main',
                        whiteSpace: 'normal',
                        wordBreak: 'break-word',
                        lineHeight: 1.4,
                        overflow: 'visible',
                        textOverflow: 'unset',
                      }}
                    >
                      {row.body}
                    </Typography>
                  </OverflowTooltip>
                );
              }
            }
            return 'No content';
          case QuoteActivityItemType.Document:
            if ('file' in row) {
              return (
                <OverflowTooltip tooltipText={row.file?.fileName || 'Document'}>
                  <Typography
                    variant='bodySmall'
                    sx={{
                      color: 'text.main',
                      whiteSpace: 'normal',
                      wordBreak: 'break-word',
                      lineHeight: 1.4,
                      overflow: 'visible',
                      textOverflow: 'unset',
                    }}
                  >
                    {row.file?.fileName || 'Document'}
                  </Typography>
                </OverflowTooltip>
              );
            }
            return 'Document';
          case QuoteActivityItemType.UnderwritingActivity: {
            const currentItem = row as NewUnderwritingActivityItem;
            const isUnresolved = !currentItem.resolvedAt;

            const activityLabel = currentItem?.underwritingActivityType?.label || 'N/A';
            const activityResolvedReason = currentItem?.resolutionReason?.label || 'N/A';
            const createdAt = currentItem.createdAt
              ? format(new Date(currentItem.createdAt), getDateFormatter(clockFormat, ShortDateFormat))
              : 'N/A';
            const resolvedAt = currentItem.resolvedAt
              ? format(new Date(currentItem.resolvedAt), getDateFormatter(clockFormat, ShortDateFormat))
              : 'N/A';

            // FUTURE FULLSCREEN TODO: tooltips have to occur if content is ellipises. This was called out at least one of the tables but seemingly needs to be applied to all as it is not working here.
            return isUnresolved
              ? `Underwriting Activity "${activityLabel}" was created.`
              : `Underwriting Activity "${activityLabel}" was resolved with reason "${activityResolvedReason}"\nActivity Time: ${createdAt} - ${resolvedAt}`;
            // FUTURE FULLSCREEN TODO: should this information be available somehow
            // {currentItem.description && (
            //   <Typography variant='bodySmall' color='font.main' mt={2} display='block'>
            //     Description: {currentItem.description}
            //   </Typography>
            // )}
            // {currentItem.note && (
            //   <Typography variant='bodySmall' color='font.main' mt={2} display='block'>
            //     Note: {currentItem.note}
            //   </Typography>
            // )}
          }

          default:
            return 'Unknown activity';
        }
      },
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 150,
      renderCell: ({ row }) => {
        if (row.activityItemType === QuoteActivityItemType.QuoteRating && isInternalUser) {
          if ('ratingDetailsFileUrl' in row && row.ratingDetailsFileUrl) {
            return <ViewQuoteRatingSummaryButton ratingDetailsFileUrl={row.ratingDetailsFileUrl} />;
          }
        }
        return null;
      },
    },
  ];
  return columns;
};
